// API functions for scenario generation
import { mockScenario, mockScene, getSceneData } from './mockData';

// Flag to toggle between API and mock data
const USE_MOCK_DATA = false; // Set to false to use the real API

// Function to extract scene information from text
export const extractSceneInfoFromText = (text) => {
  console.log('Attempting to extract scene info from text');
  
  // Create an object to store our extracted info
  const extracted = {};
  
  // Handle different possible formats
  let hasFoundStructure = false;
  
  // Format 1: Look for explicit labels
  
  // Try to extract the scene text - multiple possible formats
  let sceneMatch = text.match(/[Ss]cene:?\s*(.*?)(?=[Cc]hoice 1:|[Cc]hoice1:|[Oo]ption 1:|$)/s);
  if (!sceneMatch) {
    sceneMatch = text.match(/[Ss]cene[_ ]?[Tt]ext:?\s*(.*?)(?=[Cc]hoice 1:|[Cc]hoice1:|[Oo]ption 1:|$)/s);
  }
  
  if (sceneMatch && sceneMatch[1]) {
    extracted.scene_text = sceneMatch[1].trim();
    hasFoundStructure = true;
  }
  
  // Try to extract Choice 1 - multiple possible formats
  let choice1Match = text.match(/[Cc]hoice 1:?\s*(.*?)(?=[Cc]hoice 2:|[Cc]hoice2:|[Oo]ption 2:|$)/s);
  if (!choice1Match) {
    choice1Match = text.match(/[Cc]hoice1:?\s*(.*?)(?=[Cc]hoice 2:|[Cc]hoice2:|[Oo]ption 2:|$)/s);
  }
  if (!choice1Match) {
    choice1Match = text.match(/[Oo]ption 1:?\s*(.*?)(?=[Cc]hoice 2:|[Cc]hoice2:|[Oo]ption 2:|$)/s);
  }
  
  if (choice1Match && choice1Match[1]) {
    extracted.choice1 = choice1Match[1].trim();
    hasFoundStructure = true;
  }
  
  // Try to extract Choice 2 - multiple possible formats
  let choice2Match = text.match(/[Cc]hoice 2:?\s*(.*?)(?=[Cc]hoice 3:|[Cc]hoice3:|[Oo]ption 3:|$)/s);
  if (!choice2Match) {
    choice2Match = text.match(/[Cc]hoice2:?\s*(.*?)(?=[Cc]hoice 3:|[Cc]hoice3:|[Oo]ption 3:|$)/s);
  }
  if (!choice2Match) {
    choice2Match = text.match(/[Oo]ption 2:?\s*(.*?)(?=[Cc]hoice 3:|[Cc]hoice3:|[Oo]ption 3:|$)/s);
  }
  
  if (choice2Match && choice2Match[1]) {
    extracted.choice2 = choice2Match[1].trim();
    hasFoundStructure = true;
  }
  
  // Try to extract Choice 3 - multiple possible formats
  let choice3Match = text.match(/[Cc]hoice 3:?\s*(.*?)(?=$)/s);
  if (!choice3Match) {
    choice3Match = text.match(/[Cc]hoice3:?\s*(.*?)(?=$)/s);
  }
  if (!choice3Match) {
    choice3Match = text.match(/[Oo]ption 3:?\s*(.*?)(?=$)/s);
  }
  
  if (choice3Match && choice3Match[1]) {
    extracted.choice3 = choice3Match[1].trim();
    hasFoundStructure = true;
  }
  
  // If we couldn't find a standard structure, try alternative parsing
  if (!hasFoundStructure) {
    console.log('No standard structure found, trying alternative parsing');
    
    // Try to split by double newlines to find paragraphs
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
    
    if (paragraphs.length >= 4) {
      // Assume first paragraph is scene text
      extracted.scene_text = paragraphs[0].trim();
      
      // Assume next three paragraphs are choices
      extracted.choice1 = paragraphs[1].trim();
      extracted.choice2 = paragraphs[2].trim();
      extracted.choice3 = paragraphs[3].trim();
    } else if (paragraphs.length >= 1) {
      // If there's only one paragraph, try to look for numbered choices within it
      extracted.scene_text = paragraphs[0].trim();
      
      // Try to find numbered choices within the text
      const choicePattern = /\b(\d+[\)\.:])\s+([^\d\n]+)/g;
      let match;
      let choices = [];
      
      while ((match = choicePattern.exec(text)) !== null) {
        choices.push(match[2].trim());
      }
      
      if (choices.length >= 3) {
        extracted.choice1 = choices[0];
        extracted.choice2 = choices[1];
        extracted.choice3 = choices[2];
      }
    }
  }
  
  // If we still don't have choices, try to find bullet points
  if (!extracted.choice1 && !extracted.choice2 && !extracted.choice3) {
    const bulletChoicePattern = /[\*\-\•]\s+([^\n]+)/g;
    let match;
    let choices = [];
    
    while ((match = bulletChoicePattern.exec(text)) !== null) {
      choices.push(match[1].trim());
    }
    
    if (choices.length >= 3) {
      // Assume everything before the first bullet is the scene text
      const firstBulletIndex = text.indexOf(choices[0]);
      if (firstBulletIndex > 0) {
        extracted.scene_text = text.substring(0, firstBulletIndex).trim();
      }
      
      extracted.choice1 = choices[0];
      extracted.choice2 = choices[1];
      extracted.choice3 = choices[2];
    }
  }
  
  // Last resort: just split the text into 4 parts
  if (!extracted.scene_text && !extracted.choice1) {
    console.log('No structured scene data found, using fallback parsing');
    
    const lines = text.split(/\n/).filter(line => line.trim());
    
    if (lines.length >= 4) {
      // First line/paragraph is scene text
      extracted.scene_text = lines[0].trim();
      
      // Next three lines are choices
      extracted.choice1 = lines[1].trim();
      extracted.choice2 = lines[2].trim();
      extracted.choice3 = lines[3].trim();
    }
  }
  
  console.log('Extracted scene data:', extracted);
  
  return extracted;
};

// Function to extract role information from text format
export const extractRoleInfoFromText = (text) => {
  console.log('Attempting to extract role info from text');
  
  // Create an object to store our extracted info
  const extracted = {};
  
  // Handle different possible formats
  
  // Format 1: Look for section patterns like "Scenario Description:", "Role 1:", etc.
  let hasFoundStructure = false;
  
  // Try to extract the detailed scenario description - multiple possible formats
  let scenarioMatch = text.match(/[Ss]cenario [Dd]escription:?\s*(.*?)(?=[Rr]ole 1:|[Rr]ole1:|$)/s);
  if (!scenarioMatch) {
    scenarioMatch = text.match(/[Dd]etailed[_ ]?[Ss]cenario[_ ]?[Dd]escription:?\s*(.*?)(?=[Rr]ole 1:|[Rr]ole1:|$)/s);
  }
  
  if (scenarioMatch && scenarioMatch[1]) {
    extracted.detailed_scenario_description = scenarioMatch[1].trim();
    hasFoundStructure = true;
  }
  
  // Try to extract Role 1 - multiple possible formats
  let role1Match = text.match(/[Rr]ole 1:?\s*(.*?)(?=[Rr]ole 2:|[Rr]ole2:|$)/s);
  if (!role1Match) {
    role1Match = text.match(/[Rr]ole1:?\s*(.*?)(?=[Rr]ole 2:|[Rr]ole2:|$)/s);
  }
  
  if (role1Match && role1Match[1]) {
    const roleText = role1Match[1].trim();
    const roleLines = roleText.split(/\r?\n/).filter(line => line.trim());
    
    if (roleLines.length >= 1) {
      // First line is the name
      extracted.role1_name = roleLines[0].trim();
      // Rest is the description
      extracted.role1_description = roleLines.slice(1).join('\n').trim();
      
      // If there's no clear separation, try to split by colon
      if (!extracted.role1_description && extracted.role1_name.includes(':')) {
        const parts = extracted.role1_name.split(':');
        extracted.role1_name = parts[0].trim();
        extracted.role1_description = parts.slice(1).join(':').trim();
      }
      
      hasFoundStructure = true;
    }
  }
  
  // Try to extract Role 2 - multiple possible formats
  let role2Match = text.match(/[Rr]ole 2:?\s*(.*?)(?=[Rr]ole 3:|[Rr]ole3:|$)/s);
  if (!role2Match) {
    role2Match = text.match(/[Rr]ole2:?\s*(.*?)(?=[Rr]ole 3:|[Rr]ole3:|$)/s);
  }
  
  if (role2Match && role2Match[1]) {
    const roleText = role2Match[1].trim();
    const roleLines = roleText.split(/\r?\n/).filter(line => line.trim());
    
    if (roleLines.length >= 1) {
      // First line is the name
      extracted.role2_name = roleLines[0].trim();
      // Rest is the description
      extracted.role2_description = roleLines.slice(1).join('\n').trim();
      
      // If there's no clear separation, try to split by colon
      if (!extracted.role2_description && extracted.role2_name.includes(':')) {
        const parts = extracted.role2_name.split(':');
        extracted.role2_name = parts[0].trim();
        extracted.role2_description = parts.slice(1).join(':').trim();
      }
      
      hasFoundStructure = true;
    }
  }
  
  // Try to extract Role 3 - multiple possible formats
  let role3Match = text.match(/[Rr]ole 3:?\s*(.*?)(?=$)/s);
  if (!role3Match) {
    role3Match = text.match(/[Rr]ole3:?\s*(.*?)(?=$)/s);
  }
  
  if (role3Match && role3Match[1]) {
    const roleText = role3Match[1].trim();
    const roleLines = roleText.split(/\r?\n/).filter(line => line.trim());
    
    if (roleLines.length >= 1) {
      // First line is the name
      extracted.role3_name = roleLines[0].trim();
      // Rest is the description
      extracted.role3_description = roleLines.slice(1).join('\n').trim();
      
      // If there's no clear separation, try to split by colon
      if (!extracted.role3_description && extracted.role3_name.includes(':')) {
        const parts = extracted.role3_name.split(':');
        extracted.role3_name = parts[0].trim();
        extracted.role3_description = parts.slice(1).join(':').trim();
      }
      
      hasFoundStructure = true;
    }
  }
  
  // If we found a structure, use it; otherwise, try alternative approaches
  if (!hasFoundStructure) {
    // Check if the text contains three roles in some other format
    console.log('No standard structure found, trying alternative parsing');
    
    // Try to split by double newlines to find paragraphs
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
    
    if (paragraphs.length >= 4) {
      // Assume first paragraph is scenario description
      extracted.detailed_scenario_description = paragraphs[0].trim();
      
      // Assume next three paragraphs are roles
      for (let i = 0; i < 3; i++) {
        if (i + 1 < paragraphs.length) {
          const roleText = paragraphs[i + 1].trim();
          const roleLines = roleText.split(/\r?\n/).filter(line => line.trim());
          
          if (roleLines.length >= 1) {
            const roleKey = `role${i + 1}`;
            extracted[`${roleKey}_name`] = roleLines[0].trim();
            extracted[`${roleKey}_description`] = roleLines.slice(1).join('\n').trim();
          }
        }
      }
    }
  }
  
  console.log('Extracted data:', extracted);
  
  // Return null if we couldn't extract meaningful data
  if (!extracted.role1_name && !extracted.role2_name && !extracted.role3_name) {
    // Last resort: just split the text into four equal parts
    console.log('No structured data found, using fallback parsing');
    
    const parts = text.split(/\n/).filter(line => line.trim());
    
    if (parts.length >= 4) {
      extracted.detailed_scenario_description = parts[0].trim();
      extracted.role1_name = "Role 1";
      extracted.role1_description = parts[1].trim();
      extracted.role2_name = "Role 2"; 
      extracted.role2_description = parts[2].trim();
      extracted.role3_name = "Role 3";
      extracted.role3_description = parts[3].trim();
    } else {
      return null;
    }
  }
  
  return extracted;
};

export const generateScenario = async (questions, answers) => {
  // Use mock data if API is unavailable
  if (USE_MOCK_DATA) {
    console.log('Using mock scenario data instead of API');
    return new Promise(resolve => {
      // Simulate API delay
      setTimeout(() => resolve(mockScenario), 1500);
    });
  }

  try {
    const context = questions.map((q, i) => 
      `Question ${i+1}: ${q}\nAnswer ${i+1}: ${answers[i]}`
    ).join('\n');
    
    console.log('Sending scenario request with context:', context);
    
    const response = await fetch('https://promptmanager.fly.dev/api/v1/prompts/6/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error: ${response.status}`, errorText);
      throw new Error(`API returned status ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Received scenario data:', data);
    
    if (!data.content) {
      throw new Error('API returned empty content. The response was: ' + JSON.stringify(data));
    }
    
    // Let's log and inspect the parsed content
    console.log('API content:', data.content);
    
    // Try to parse the content if it's a string
    let content = data.content;
    if (typeof data.content === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsedContent = JSON.parse(data.content);
        console.log('Parsed content:', parsedContent);
        content = parsedContent;
      } catch (e) {
        // It's not JSON, might be a plain text format
        console.log('Content is not JSON, using as is.');
        
        // Try to extract role information from the text format
        const extractedContent = extractRoleInfoFromText(data.content);
        if (extractedContent) {
          console.log('Extracted content:', extractedContent);
          content = extractedContent;
        }
      }
    }
    
    return content;
  } catch (error) {
    console.error('Error in generateScenario:', error);
    throw error;
  }
};

export const generateScene = async (scenario, selectedRole, scenes, userChoices) => {
  // Use mock data if API is unavailable
  if (USE_MOCK_DATA) {
    console.log('Using mock scene data instead of API');
    console.log('User choices so far:', userChoices);
    
    return new Promise(resolve => {
      // Get the appropriate scene based on the current scene number and previous choice
      const nextSceneNumber = scenes.length + 1;
      
      // Get the most recent choice to determine the next scene
      const lastChoice = userChoices.length > 0 ? userChoices[userChoices.length - 1] : null;
      
      // Log the choice that will influence the next scene
      console.log(`Generating scene ${nextSceneNumber} based on previous choice:`, lastChoice);
      
      // Get scene data based on scene number and last choice
      const sceneData = getSceneData(nextSceneNumber, lastChoice);
      
      // Simulate API delay
      setTimeout(() => resolve(sceneData), 1500);
    });
  }
  
  try {
    // Validate inputs
    console.log("Validating inputs for scene generation:", { 
      scenarioExists: !!scenario, 
      roleExists: !!selectedRole,
      scenesExists: !!scenes,
      scenesLength: scenes?.length,
      userChoicesExists: !!userChoices,
      userChoicesLength: userChoices?.length
    });
    
    if (!scenario || !scenario.detailed_scenario_description) {
      console.error("Missing scenario description:", scenario);
      throw new Error("Missing scenario description");
    }
    
    if (!selectedRole) {
      console.error("Missing selected role");
      throw new Error("No role selected");
    }
    
    if (!selectedRole.name || !selectedRole.description) {
      console.error("Selected role has missing data:", selectedRole);
      // Try to repair the role if possible
      selectedRole.name = selectedRole.name || "Player Role";
      selectedRole.description = selectedRole.description || "A role in this future scenario";
    }
    
    // Build context with scenario description, chosen role, and previous scenes/choices
    let context = `Scenario Description: ${scenario.detailed_scenario_description}\n`;
    context += `Chosen Role: ${selectedRole.name} - ${selectedRole.description}\n`;
    
    // Add previous scenes and choices if they exist, including rejected choices
    if (scenes && Array.isArray(scenes)) {
      // First, add all completed scenes (where a choice has been made)
      for (let i = 0; i < scenes.length; i++) {
        // For all scenes where the user has made a choice
        const hasUserMadeChoice = userChoices && i < userChoices.length;
        
        if (scenes[i] && scenes[i].text) {
          context += `Scene ${i+1}: ${scenes[i].text}\n`;
          
          // Add the selected choice if user made one for this scene
          if (hasUserMadeChoice) {
            context += `Selected Choice from Scene ${i+1}: ${userChoices[i]}\n`;
            
            // Add rejected choices if available
            if (scenes[i].choices && Array.isArray(scenes[i].choices) && scenes[i].choices.length > 1) {
              context += `Rejected Choices from Scene ${i+1}:\n`;
              
              // Add each rejected choice (ones not matching the user's selection)
              let rejectedChoices = scenes[i].choices.filter(choice => choice !== userChoices[i]);
              
              // Add each rejected choice as a bullet point
              rejectedChoices.forEach(choice => {
                context += `- ${choice}\n`;
              });
            }
          }
        }
      }
    }
    
    // Log the detailed context for debugging
    console.log('Detailed context being sent to API:', context);

    console.log('Sending scene request with context:', context);

    const response = await fetch('https://promptmanager.fly.dev/api/v1/prompts/7/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error: ${response.status}`, errorText);
      throw new Error(`API returned status ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Received scene data:', data);
    
    if (!data.content) {
      throw new Error('API returned empty content. The response was: ' + JSON.stringify(data));
    }
    
    // Log the content for debugging
    console.log('API scene content:', data.content);
    
    // Try to parse the content if it's a string
    let content = data.content;
    if (typeof data.content === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsedContent = JSON.parse(data.content);
        console.log('Parsed scene content:', parsedContent);
        content = parsedContent;
      } catch (e) {
        // It's not JSON, might be a plain text format
        console.log('Scene content is not JSON, using as is.');
        
        // Try to extract scene information from the text format
        const extractedContent = extractSceneInfoFromText(data.content);
        if (extractedContent) {
          console.log('Extracted scene content:', extractedContent);
          content = extractedContent;
        }
      }
    }
    
    // If we still don't have proper scene content, create a fallback
    if (!content || (typeof content === 'object' && !content.scene_text && !content.choice1)) {
      console.warn('Failed to get valid scene data, using fallback');
      
      // Create a fallback scene with the raw content
      content = {
        scene_text: typeof data.content === 'string' ? data.content : 'The story continues...',
        choice1: 'Continue the adventure',
        choice2: 'Explore a different path',
        choice3: 'Take a calculated risk'
      };
    }
    
    return content;
  } catch (error) {
    console.error('Error in generateScene:', error);
    throw error;
  }
};
